import React from 'react';
import {
  Box,
  Checkbox,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
} from '@mui/material';

export type CheckboxListOption<T extends string | number> = {
  value: T;
  label: React.ReactNode;
  disabled?: boolean;
};

export type CheckboxMultiSelectListProps<T extends string | number> = {
  label?: string;
  options: CheckboxListOption<T>[];
  value: T[];
  onChange: (next: T[]) => void;
  disabled?: boolean;
  dense?: boolean;
  maxHeight?: number | string;
  width?: number | string;
};

export const MultipleList = <T extends string | number>({
  label,
  options,
  value,
  onChange,
  disabled = false,
  dense = true,
  maxHeight = 350,
  width = '100%',
}: CheckboxMultiSelectListProps<T>) => {
  const valueSet = React.useMemo(() => new Set<T>(value), [value]);

  const toggle = (v: T) => {
    if (disabled) return;
    if (valueSet.has(v)) onChange(value.filter((x) => x !== v));
    else onChange([...value, v]);
  };

  return (
    <Box sx={{ width }}>
      {label && (
        <div style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 'bold' }}>
          {label}:
        </div>
      )}

      <Paper variant="outlined" sx={{ overflow: 'auto', maxHeight, background: 'transparent' }}>
        <List dense={dense} disablePadding>
          {options.map((o) => {
            const checked = valueSet.has(o.value);
            const itemDisabled = disabled || !!o.disabled;
            return (
              <ListItemButton
                key={String(o.value)}
                onClick={() => toggle(o.value)}
                disabled={itemDisabled}
                sx={{ px: 1.25 }}
              >
                <ListItemIcon sx={{ minWidth: 34 }}>
                  <Checkbox edge="start" size="small" checked={checked} tabIndex={-1} disableRipple />
                </ListItemIcon>
                <ListItemText primary={o.label} />
              </ListItemButton>
            );
          })}
        </List>
      </Paper>
    </Box>
  );
};